import { docPermConstants, linkPermConstants } from "../_constants/permissionConstants";
import { DOCTYPE } from "../_constants/docTypeConstants";

/*
 * Role Permissions
 *   Declare each user role's abilities in terms of docPermConstants.
 *   Permissions are accessible by DOCTYPE constants in bracket notations
 *   for each role. (For example, rolePermissions[projRoleClass][docType])
 */

const allPermissions = Object.values(docPermConstants);
const {
  CREATE,
  CREATE_LEVEL_2,
  VIEW,
  EDIT,
  EDIT_LEVEL_2,
  EDIT_LEVEL_3,
  AUTHOR_EDIT,
  DELETE,
  AUTHOR_DELETE,
  COPY,
  PROJECT_APPROVE,
  VALIDATE,
} = docPermConstants;
const { OPEN } = linkPermConstants;

// Set default of zero permissions for each docType.
// Serves as a fallback for any non-specified docTypes in each role
// (i.e. unless explicitly specified, permission is not granted.)
const roleTemplate = {
  [DOCTYPE.LINK]: [],
  [DOCTYPE.ADMIN_TASKS]: [],
  [DOCTYPE.ADR]: [],
  [DOCTYPE.ADR_LIST]: [],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [],
  [DOCTYPE.REPORT_LABOR_WEEKLY]: [CREATE, VIEW],
  [DOCTYPE.REPORT_CONTRACTOR_WEEKLY]: [CREATE, VIEW],
  [DOCTYPE.REPORT_ITEM_EST_QUANTITIES]: [CREATE, VIEW],
  [DOCTYPE.REPORT_PAYMENT_STATUS]: [CREATE, VIEW],
  [DOCTYPE.REPORT_COMMENT_KEYWORD]: [CREATE, VIEW],
  [DOCTYPE.REPORT_OVERTIME]: [CREATE, VIEW],
  [DOCTYPE.REPORT_WSWD]: [CREATE, VIEW],
  [DOCTYPE.REPORT_CCO]: [CREATE, VIEW],
  [DOCTYPE.REPORT_SELECTION]: [VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [],
  [DOCTYPE.PROGRESS_PAYMENTS]: [],
  [DOCTYPE.PROJECT_SETUP]: [],
  [DOCTYPE.PROJECT_DASH]: [],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [],
  [DOCTYPE.PROJECT_LIST]: [],
};

const UNVERIFIED = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [],
  [DOCTYPE.ADR]: [VIEW],
  [DOCTYPE.ADR_LIST]: [VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [VIEW],
  [DOCTYPE.PROGRESS_PAYMENTS]: [VIEW],
  [DOCTYPE.PROJECT_SETUP]: [VIEW],
  [DOCTYPE.PROJECT_DASH]: [VIEW],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// Assist. RE/SR
const ARESR = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [VIEW],
  [DOCTYPE.ADR]: [CREATE, VIEW, AUTHOR_EDIT, AUTHOR_DELETE, COPY],
  [DOCTYPE.ADR_LIST]: [CREATE, VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [CREATE, VIEW, EDIT, COPY],
  [DOCTYPE.PROGRESS_PAYMENTS]: [CREATE, VIEW, EDIT, DELETE, COPY],
  [DOCTYPE.PROJECT_SETUP]: [CREATE, VIEW, EDIT],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// Permissions for access into project data outside of user's project assignments
const OE = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [VIEW],
  [DOCTYPE.ADR]: [CREATE, VIEW, AUTHOR_EDIT, AUTHOR_DELETE, COPY, PROJECT_APPROVE],
  [DOCTYPE.ADR_LIST]: [CREATE, VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [CREATE, VIEW, EDIT, COPY],
  [DOCTYPE.PROGRESS_PAYMENTS]: [CREATE, VIEW, EDIT, DELETE, COPY],
  [DOCTYPE.PROJECT_SETUP]: [CREATE, CREATE_LEVEL_2, VIEW, EDIT, EDIT_LEVEL_2, DELETE, VALIDATE],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [CREATE, VIEW, EDIT, EDIT_LEVEL_2, DELETE],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// RE/SR
const RESR = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [VIEW],
  [DOCTYPE.ADR]: [CREATE, VIEW, AUTHOR_EDIT, AUTHOR_DELETE, COPY, PROJECT_APPROVE],
  [DOCTYPE.ADR_LIST]: [CREATE, VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [CREATE, VIEW, EDIT, COPY],
  [DOCTYPE.PROGRESS_PAYMENTS]: [CREATE, VIEW, EDIT, DELETE, COPY],
  [DOCTYPE.PROJECT_SETUP]: [CREATE, CREATE_LEVEL_2, VIEW, EDIT, EDIT_LEVEL_2, DELETE, VALIDATE],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// RE_Auth/SR_Auth
const RESRA = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [VIEW],
  [DOCTYPE.ADR]: [CREATE, VIEW, AUTHOR_EDIT, AUTHOR_DELETE, COPY, PROJECT_APPROVE],
  [DOCTYPE.ADR_LIST]: [CREATE, VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [CREATE, VIEW, EDIT, COPY],
  [DOCTYPE.PROGRESS_PAYMENTS]: [CREATE, VIEW, EDIT, DELETE, COPY],
  [DOCTYPE.PROJECT_SETUP]: [CREATE, CREATE_LEVEL_2, VIEW, EDIT, EDIT_LEVEL_2, DELETE, VALIDATE],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// Construction Engineer / Bridge Const Engineer
const CEBCE = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [VIEW],
  [DOCTYPE.ADR]: [CREATE, VIEW, AUTHOR_EDIT, AUTHOR_DELETE, COPY, PROJECT_APPROVE],
  [DOCTYPE.ADR_LIST]: [CREATE, VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [CREATE, VIEW, EDIT, COPY],
  [DOCTYPE.PROGRESS_PAYMENTS]: [CREATE, VIEW],
  [DOCTYPE.PROJECT_SETUP]: [CREATE, CREATE_LEVEL_2, VIEW, EDIT, EDIT_LEVEL_2, DELETE, VALIDATE],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW, EDIT, EDIT_LEVEL_2],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// Senior RE/SR
const CEBCERESR = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [VIEW],
  [DOCTYPE.ADR]: [CREATE, VIEW, AUTHOR_EDIT, AUTHOR_DELETE, COPY, PROJECT_APPROVE],
  [DOCTYPE.ADR_LIST]: [CREATE, VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [CREATE, VIEW, EDIT, COPY],
  [DOCTYPE.PROGRESS_PAYMENTS]: [CREATE, VIEW, EDIT, DELETE, COPY],
  [DOCTYPE.PROJECT_SETUP]: [CREATE, CREATE_LEVEL_2, VIEW, EDIT, EDIT_LEVEL_2, DELETE, VALIDATE],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW, EDIT, EDIT_LEVEL_2],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// Construction Manager / Area Construction Manager (OSC)
const CMACM = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [VIEW, EDIT],
  [DOCTYPE.ADR]: [VIEW],
  [DOCTYPE.ADR_LIST]: [VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [CREATE, VIEW, EDIT, COPY],
  [DOCTYPE.PROGRESS_PAYMENTS]: [CREATE, VIEW],
  [DOCTYPE.PROJECT_SETUP]: [VIEW, EDIT, DELETE],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW, EDIT, EDIT_LEVEL_2],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// Permissions for access into project data outside of user's project assignments
const NONPROJECT_STAFF = {
  ...roleTemplate,
  [DOCTYPE.ADMIN_TASKS]: [],
  [DOCTYPE.ADR]: [VIEW],
  [DOCTYPE.ADR_LIST]: [VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [VIEW],
  [DOCTYPE.PROGRESS_PAYMENTS]: [VIEW],
  [DOCTYPE.PROJECT_SETUP]: [VIEW],
  [DOCTYPE.PROJECT_DASH]: [VIEW],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
  [DOCTYPE.LINK]: [OPEN],
};

const NONPROJECT_SUPERVISOR = {
  ...roleTemplate,
  [DOCTYPE.ADMIN_TASKS]: [],
  [DOCTYPE.ADR]: [VIEW],
  [DOCTYPE.ADR_LIST]: [VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [VIEW],
  [DOCTYPE.PROGRESS_PAYMENTS]: [VIEW],
  [DOCTYPE.PROJECT_SETUP]: [VIEW],
  [DOCTYPE.PROJECT_DASH]: [VIEW],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [VIEW],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
  [DOCTYPE.LINK]: [OPEN],
};

const DISTADMIN = {
  ...roleTemplate,
  [DOCTYPE.ADMIN_TASKS]: [VIEW, EDIT, EDIT_LEVEL_2],
  [DOCTYPE.ADR]: [CREATE, VIEW, AUTHOR_EDIT, AUTHOR_DELETE, COPY, PROJECT_APPROVE],
  [DOCTYPE.ADR_LIST]: [CREATE, VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [VIEW],
  [DOCTYPE.PROGRESS_PAYMENTS]: [VIEW, CREATE, CREATE_LEVEL_2],
  [DOCTYPE.PROJECT_SETUP]: [
    CREATE,
    CREATE_LEVEL_2,
    VIEW,
    EDIT,
    EDIT_LEVEL_2,
    EDIT_LEVEL_3,
    DELETE,
    VALIDATE,
  ],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [CREATE, VIEW, EDIT, EDIT_LEVEL_2, EDIT_LEVEL_3, DELETE],
  [DOCTYPE.PROJECT_LIST]: [CREATE, CREATE_LEVEL_2, VIEW, EDIT, EDIT_LEVEL_2, DELETE],
  [DOCTYPE.LINK]: [OPEN],
};

// For legacy use only. "Office admins" now refer only to district admins, and not office
// engineers. So OA's permissions are now covered by the new DISTADMIN role.
// OA here is still necessary since cached userDto's projectRoleClass and projectRoleSymbol
// may still have "OA"s. Since most of the legacy use of OA was for office engineers,
// same permissions as the new OE class are granted here.
const OA = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [VIEW],
  [DOCTYPE.ADR]: [CREATE, VIEW, AUTHOR_EDIT, AUTHOR_DELETE, COPY, PROJECT_APPROVE],
  [DOCTYPE.ADR_LIST]: [CREATE, VIEW],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [CREATE, VIEW],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [CREATE, VIEW, EDIT, COPY],
  [DOCTYPE.PROGRESS_PAYMENTS]: [CREATE, VIEW, EDIT, DELETE, COPY],
  [DOCTYPE.PROJECT_SETUP]: [CREATE, CREATE_LEVEL_2, VIEW, EDIT, EDIT_LEVEL_2, DELETE],
  [DOCTYPE.PROJECT_DASH]: [VIEW, EDIT],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [CREATE, VIEW, EDIT, EDIT_LEVEL_2, DELETE],
  [DOCTYPE.PROJECT_LIST]: [VIEW],
};

// System admin
// DOCTYPE.ADMIN_TASKS: EDIT_LEVEL_2 provides inter-district editability
const SYSADMIN = {
  ...roleTemplate,
  [DOCTYPE.LINK]: [OPEN],
  [DOCTYPE.ADMIN_TASKS]: [...allPermissions],
  [DOCTYPE.ADR]: [...allPermissions],
  [DOCTYPE.ADR_LIST]: [...allPermissions],
  [DOCTYPE.ADR_SEARCH_RESULTS]: [...allPermissions],
  [DOCTYPE.WEEKLY_NEWSLETTER]: [...allPermissions],
  [DOCTYPE.PROGRESS_PAYMENTS]: [...allPermissions],
  [DOCTYPE.PROJECT_SETUP]: [...allPermissions],
  [DOCTYPE.PROJECT_DASH]: [...allPermissions],
  [DOCTYPE.PROJECT_ASSIGNMENT]: [...allPermissions],
  [DOCTYPE.PROJECT_LIST]: [...allPermissions],
};

export const rolePermissions = {
  UNVERIFIED,
  ARESR,
  RESR,
  RESRA,
  OE,
  CEBCE,
  CEBCERESR,
  CMACM,
  NONPROJECT_STAFF,
  NONPROJECT_SUPERVISOR,
  DISTADMIN,
  SYSADMIN,
  OA, // Legacy use only. See comments.
};
